import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { logsApi } from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Loader2, Download, Eye, Search, FileText, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'
import type { LogFile, LogEntry } from '@/types'

const levelColors: Record<string, string> = {
  DEBUG: 'bg-gray-100 text-gray-800',
  INFO: 'bg-blue-100 text-blue-800',
  WARN: 'bg-yellow-100 text-yellow-800',
  ERROR: 'bg-red-100 text-red-800',
  FATAL: 'bg-red-200 text-red-900',
}

function LogEntryRow({ entry }: { entry: LogEntry }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setExpanded(!expanded)}>
        <TableCell className="font-mono text-xs whitespace-nowrap">
          {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '-'}
        </TableCell>
        <TableCell>
          <Badge className={levelColors[entry.level] || 'bg-gray-100'}>{entry.level || 'LOG'}</Badge>
        </TableCell>
        <TableCell className="max-w-md truncate">{entry.message}</TableCell>
        <TableCell className="text-xs text-muted-foreground">{entry.caller}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={4} className="bg-muted/30">
            <pre className="text-xs font-mono whitespace-pre-wrap p-2 max-h-48 overflow-auto">
              {entry.raw || JSON.stringify(entry, null, 2)}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export default function LogViewerPage() {
  const [selectedModule, setSelectedModule] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<LogFile | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [levelFilter, setLevelFilter] = useState<string>('')
  const [page, setPage] = useState(1)

  const { data: logList, isLoading: listLoading, refetch } = useQuery({
    queryKey: ['log-files', selectedModule],
    queryFn: () => logsApi.listFiles({ module: selectedModule || undefined }),
  })

  const { data: logContent, isLoading: contentLoading } = useQuery({
    queryKey: ['log-content', selectedFile?.path, searchQuery, levelFilter, page],
    queryFn: () => logsApi.getContent(selectedFile!.path, {
      search: searchQuery || undefined,
      level: levelFilter || undefined,
      page,
      page_size: 50,
    }),
    enabled: !!selectedFile && viewerOpen,
  })

  const handleViewLog = (file: LogFile) => {
    setSelectedFile(file)
    setPage(1)
    setViewerOpen(true)
  }

  const handleDownload = (file: LogFile) => {
    const url = logsApi.getDownloadUrl(file.path)
    window.open(url, '_blank')
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Log Viewer</h1>
          <p className="text-muted-foreground">View and download application logs organized by module and date</p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Select value={selectedModule || "all"} onValueChange={(v) => setSelectedModule(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {logList?.modules.map((module) => (
                  <SelectItem key={module} value={module}>{module}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Log Files List */}
      <Card>
        <CardHeader>
          <CardTitle>Log Files</CardTitle>
          <CardDescription>
            {logList?.total || 0} log files found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : logList?.files.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No log files found</p>
              <p className="text-sm">Enable file logging in the backend to start collecting logs</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Last Modified</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logList?.files.map((file) => (
                  <TableRow key={file.path}>
                    <TableCell>
                      <Badge variant="outline">{file.module}</Badge>
                    </TableCell>
                    <TableCell>{file.date}</TableCell>
                    <TableCell>{file.size_human}</TableCell>
                    <TableCell>{new Date(file.modified_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleViewLog(file)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDownload(file)}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Log Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedFile?.module} / {selectedFile?.date}
              <Badge variant="outline" className="ml-2">{selectedFile?.size_human}</Badge>
            </DialogTitle>
          </DialogHeader>

          {/* Filters */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
                className="pl-9"
              />
            </div>
            <Select value={levelFilter || "all"} onValueChange={(v) => { setLevelFilter(v === "all" ? "" : v); setPage(1) }}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="DEBUG">DEBUG</SelectItem>
                <SelectItem value="INFO">INFO</SelectItem>
                <SelectItem value="WARN">WARN</SelectItem>
                <SelectItem value="ERROR">ERROR</SelectItem>
                <SelectItem value="FATAL">FATAL</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => selectedFile && handleDownload(selectedFile)}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>

          {/* Log Content */}
          <ScrollArea className="h-[60vh] border rounded-md">
            {contentLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-44">Timestamp</TableHead>
                    <TableHead className="w-20">Level</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="w-48">Caller</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logContent?.entries.map((entry, idx) => (
                    <LogEntryRow key={idx} entry={entry} />
                  ))}
                  {logContent?.entries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No log entries found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </ScrollArea>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Page {logContent?.page || 1} • {logContent?.total_lines || 0} total entries
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!logContent?.has_more}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

